import { useEffect, useState } from 'react';
import { useDevice } from '../../store';
import { useToasts } from '../../lib/toast';
import { api } from '../../lib/api';
import { TopBar } from '../TopBar';
import { LoginGate } from '../LoginGate';
import { CarteManager } from './CarteManager';
import { PrintersManager } from './PrintersManager';
import { ReadersManager } from './ReadersManager';
import { DeliveryManager } from './DeliveryManager';
import { StockManager } from './StockManager';
import { ShoppingListManager } from './ShoppingListManager';
import { LoyaltyManager } from './LoyaltyManager';
import { PromotionsManager } from './PromotionsManager';
import { PartnersManager } from './PartnersManager';
import { ClotureManager } from './ClotureManager';
import { AuditManager } from './AuditManager';
import { StaffManager } from './StaffManager';
import { MessagingManager } from './MessagingManager';
import { ManagerDashboard } from './ManagerDashboard';
import type { RestaurantInfo } from '@taj/types';

const SECTIONS = [
  { icon: '🍽️', t: 'Carte', d: 'Plats, prix, photos, badges, disponibilité' },
  { icon: '📂', t: 'Catégories', d: 'KDS & imprimante par catégorie' },
  { icon: '📦', t: 'Stock', d: 'Temps réel, seuils d’alerte' },
  { icon: '🛒', t: 'Courses', d: 'Liste de courses à imprimer' },
  { icon: '🏷️', t: 'Promotions', d: 'Remises % / €' },
  { icon: '🎁', t: 'Fidélité', d: 'Points & récompenses' },
  { icon: '💬', t: 'Campagne SMS', d: 'SMS & email promo aux clients' },
  { icon: '🛵', t: 'Livraison', d: 'Zones, frais, minimum, gratuité' },
  { icon: '🖨️', t: 'Imprimantes', d: 'IP / ESC-POS par poste' },
  { icon: '💳', t: 'Terminaux CB', d: 'Lecteurs SumUp par poste' },
  { icon: '🤝', t: 'Partenaires', d: 'Uber Eats / Deliveroo — CA & commission' },
  { icon: '📊', t: 'Comptabilité', d: 'HT/TVA/TTC, export CSV/Excel' },
  { icon: '🧾', t: 'Clôture', d: 'Ticket Z, NF525' },
  { icon: '📜', t: 'Audit', d: 'Annulations / paiements horodatés' },
  { icon: '👥', t: 'Comptes & droits', d: 'RBAC, PIN, permissions' },
];

const FIELDS: { key: 'name' | 'tagline' | 'address' | 'phone' | 'web'; label: string; hint: string }[] = [
  { key: 'name', label: 'Nom du restaurant', hint: 'TAJ FUSION' },
  { key: 'tagline', label: 'Sous-titre', hint: 'THAÏ · INDIEN · NAAN FUSION' },
  { key: 'address', label: 'Adresse', hint: '151 RUE DE VESLE · 51100 REIMS' },
  { key: 'phone', label: 'Téléphone · RCS (ligne du ticket)', hint: 'Tél. … · RCS …' },
  { key: 'web', label: 'Site web', hint: 'WWW.TAJFUSION.COM' },
];

const SOCIAL_FIELDS: { key: 'instagram' | 'facebook' | 'tiktok'; label: string; hint: string }[] = [
  { key: 'instagram', label: '📸 Instagram (lien complet)', hint: 'https://www.instagram.com/…' },
  { key: 'facebook', label: '👍 Facebook (lien complet)', hint: 'https://www.facebook.com/…' },
  { key: 'tiktok', label: '🎵 TikTok (lien complet)', hint: 'https://www.tiktok.com/@…' },
];

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export function BackOffice() {
  const auth = useDevice((s) => s.auth);
  const setAuth = useDevice((s) => s.setAuth);
  const push = useToasts((s) => s.push);
  const [info, setInfo] = useState<RestaurantInfo | null>(null);
  const [saving, setSaving] = useState(false);
  // Lien direct #imprimantes → ouvre l'écran Imprimantes tout de suite.
  const hash = location.hash.toLowerCase();
  const wantPrinters = hash.includes('imprimante');
  const wantDelivery = hash.includes('livraison');
  const wantReaders = hash.includes('terminal');
  const [view, setView] = useState<'home' | 'carte' | 'categories' | 'stock' | 'shopping' | 'loyalty' | 'promos' | 'partners' | 'cloture' | 'audit' | 'staff' | 'messaging' | 'printers' | 'readers' | 'delivery'>(
    wantPrinters ? 'printers' : wantReaders ? 'readers' : wantDelivery ? 'delivery' : 'home',
  );
  const [screen, setScreen] = useState<'dash' | 'manage'>(wantPrinters || wantDelivery ? 'manage' : 'dash');
  const [dashTab, setDashTab] = useState<string | undefined>();

  const canBO = auth && (auth.staff.role === 'ADMIN' || auth.staff.permissions.backOffice);

  useEffect(() => {
    if (canBO) api.getSettings().then(setInfo).catch((e) => push(e.message, 'warn'));
  }, [canBO, push]);

  if (!auth) return <LoginGate mode="admin" />;
  if (!canBO) {
    return (
      <div className="kiosk">
        <TopBar />
        <div className="no-data">Accès back-office non autorisé pour ce compte.</div>
        <button className="linkbtn" style={{ alignSelf: 'center' }} onClick={() => setAuth(null)}>Se déconnecter</button>
      </div>
    );
  }

  // Tableau de bord Manager par défaut ; « Général » mène aux réglages éditables.
  if (screen === 'dash')
    return (
      <ManagerDashboard
        key={dashTab ?? 'Jour'}
        initialTab={dashTab}
        onGeneral={() => { setDashTab(undefined); setScreen('manage'); }}
        onPrinters={() => { setScreen('manage'); setView('printers'); }}
        onDelivery={() => { setScreen('manage'); setView('delivery'); }}
        onStaff={() => { setScreen('manage'); setView('staff'); }}
      />
    );

  const save = async () => {
    if (!info) return;
    setSaving(true);
    try {
      await api.updateSettings(info);
      push('✅ Coordonnées enregistrées — le ticket est à jour');
    } catch (e: any) {
      push(e.message, 'warn');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kiosk wide">
      <TopBar right={<span className="kbar-chip">🛡️ {auth.staff.fullName}</span>} />
      <div className="staff-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="km-back" onClick={() => setScreen('dash')}>←</button>
        <div>
          <div className="staff-title">⚙️ Général</div>
          <div className="staff-subtitle">Réglages & gestion — pilotage du restaurant</div>
        </div>
      </div>

      {view === 'carte' || view === 'categories' ? (
        <CarteManager onBack={() => setView('home')} openCats={view === 'categories'} />
      ) : view === 'stock' ? (
        <StockManager onBack={() => setView('home')} />
      ) : view === 'shopping' ? (
        <ShoppingListManager onBack={() => setView('home')} />
      ) : view === 'loyalty' ? (
        <LoyaltyManager onBack={() => setView('home')} />
      ) : view === 'promos' ? (
        <PromotionsManager onBack={() => setView('home')} />
      ) : view === 'partners' ? (
        <PartnersManager onBack={() => setView('home')} />
      ) : view === 'cloture' ? (
        <ClotureManager onBack={() => setView('home')} />
      ) : view === 'audit' ? (
        <AuditManager onBack={() => setView('home')} />
      ) : view === 'staff' ? (
        <StaffManager onBack={() => setView('home')} />
      ) : view === 'messaging' ? (
        <MessagingManager onBack={() => setView('home')} />
      ) : view === 'printers' ? (
        <PrintersManager onBack={() => setView('home')} />
      ) : view === 'readers' ? (
        <ReadersManager onBack={() => setView('home')} />
      ) : view === 'delivery' ? (
        <DeliveryManager onBack={() => setView('home')} />
      ) : (
      <div className="staff-scroll">
        {/* ── Coordonnées éditables (apparaissent sur le ticket) ── */}
        <div className="cc" style={{ marginBottom: 18 }}>
          <div className="staff-title" style={{ fontSize: '1.3rem', marginBottom: 4 }}>🧾 Coordonnées & ticket</div>
          <div className="staff-subtitle" style={{ marginBottom: 14 }}>
            Ces infos s’affichent sur le ticket de commande et sur le site public (horaires, réseaux). Modifie-les puis enregistre.
          </div>
          {!info ? (
            <div className="no-data">Chargement…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 520 }}>
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>
                    {f.label}
                  </div>
                  <input
                    className="inp"
                    value={info[f.key]}
                    placeholder={f.hint}
                    onChange={(e) => setInfo({ ...info, [f.key]: e.target.value })}
                  />
                </div>
              ))}

              {/* ── Horaires (affichés sur le site) ── */}
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  🕒 Horaires du site — laisse vide un jour de fermeture
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {DAYS.map((d, i) => {
                    const hrs = info.hours ?? ['', '', '', '', '', '', ''];
                    return (
                      <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 92, fontWeight: 700, fontSize: '.86rem' }}>{d}</span>
                        <input
                          className="inp"
                          style={{ flex: 1 }}
                          value={hrs[i] ?? ''}
                          placeholder="Fermé"
                          onChange={(e) => {
                            const next = [...hrs];
                            next[i] = e.target.value;
                            setInfo({ ...info, hours: next });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: '.72rem', color: 'var(--ink-dim)', marginTop: 5 }}>
                  Format libre, ex. « 11:30 – 14:30 · 18:30 – 23:30 ».
                </div>
              </div>

              {/* ── Réseaux sociaux (affichés sur le site) ── */}
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  🔗 Réseaux sociaux — laisse vide pour masquer le bouton
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {SOCIAL_FIELDS.map((f) => (
                    <div key={f.key}>
                      <div style={{ fontSize: '.72rem', fontWeight: 700, color: 'var(--ink-dim)', marginBottom: 4 }}>{f.label}</div>
                      <input
                        className="inp"
                        value={info[f.key] ?? ''}
                        placeholder={f.hint}
                        onChange={(e) => setInfo({ ...info, [f.key]: e.target.value })}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Commande en ligne : ouverture / fermeture exceptionnelle ── */}
              <div style={{ marginTop: 8, padding: 12, borderRadius: 12, background: info.onlineOrderingOpen === false ? '#fdecec' : '#eafaf0', border: `1px solid ${info.onlineOrderingOpen === false ? '#f2b8b8' : '#aee3bf'}` }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={info.onlineOrderingOpen !== false} onChange={(e) => setInfo({ ...info, onlineOrderingOpen: e.target.checked })} style={{ width: 22, height: 22, accentColor: 'var(--gold)' }} />
                  <span style={{ fontWeight: 900, fontSize: '1rem' }}>
                    {info.onlineOrderingOpen === false ? '🔴 Commande en ligne FERMÉE' : '🟢 Commande en ligne OUVERTE'}
                  </span>
                </label>
                <div style={{ fontSize: '.8rem', color: 'var(--ink-dim)', marginTop: 5 }}>
                  Décoche pour une <b>fermeture exceptionnelle</b> : le site n’accepte plus aucune commande (emporter + livraison). N’oublie pas d’enregistrer.
                </div>
              </div>

              {/* ── Modes de paiement du site (activer / désactiver) ── */}
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  💳 Paiement sur le site — active / désactive
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={info.onlinePayCash !== false} onChange={(e) => setInfo({ ...info, onlinePayCash: e.target.checked })} style={{ width: 20, height: 20, accentColor: 'var(--gold)' }} />
                  <span style={{ fontWeight: 700 }}>💵 Espèces <span style={{ color: 'var(--ink-dim)', fontWeight: 400, fontSize: '.82rem' }}>— paiement au retrait / à la livraison</span></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={info.onlinePayCard !== false} onChange={(e) => setInfo({ ...info, onlinePayCard: e.target.checked })} style={{ width: 20, height: 20, accentColor: 'var(--gold)' }} />
                  <span style={{ fontWeight: 700 }}>💳 Carte Bleue & Carte ticket <span style={{ color: 'var(--ink-dim)', fontWeight: 400, fontSize: '.82rem' }}>— paiement au retrait / à la livraison (ou en ligne si SumUp configuré)</span></span>
                </label>
              </div>

              {/* ── Borne (kiosk) : proposer ou non « Sur place » ── */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  🛎️ Borne — type de commande
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={info.kioskDineIn !== false} onChange={(e) => setInfo({ ...info, kioskDineIn: e.target.checked })} style={{ width: 20, height: 20, accentColor: 'var(--gold)' }} />
                  <span style={{ fontWeight: 700 }}>🍽️ Sur place <span style={{ color: 'var(--ink-dim)', fontWeight: 400, fontSize: '.82rem' }}>— si décoché, la borne ne propose que « À emporter »</span></span>
                </label>
              </div>

              {/* ── Commandes demandées pour plus tard (heure choisie sur le site) ── */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  ⏰ Commandes pour plus tard
                </div>
                <div style={{ fontSize: '.8rem', color: 'var(--ink-dim)', lineHeight: 1.5, marginBottom: 8 }}>
                  Quand un client commande sur le site pour une heure précise (« je passe à 20h »),
                  quand la cuisine doit-elle la recevoir ?
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={(info.scheduledMode ?? 'NOW') === 'NOW'}
                    onChange={() => setInfo({ ...info, scheduledMode: 'NOW' })}
                    style={{ width: 20, height: 20, accentColor: 'var(--gold)', marginTop: 2 }}
                  />
                  <span style={{ fontWeight: 700 }}>
                    Tout de suite
                    <span style={{ color: 'var(--ink-dim)', fontWeight: 400, fontSize: '.82rem' }}>
                      {' '}— la commande arrive en cuisine immédiatement, avec « ⏰ POUR 20:00 » en rouge sur l’écran
                    </span>
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={info.scheduledMode === 'AUTO'}
                    onChange={() => setInfo({ ...info, scheduledMode: 'AUTO' })}
                    style={{ width: 20, height: 20, accentColor: 'var(--gold)', marginTop: 2 }}
                  />
                  <span style={{ fontWeight: 700 }}>
                    Automatiquement avant l’heure
                    <span style={{ color: 'var(--ink-dim)', fontWeight: 400, fontSize: '.82rem' }}>
                      {' '}— la commande est mise de côté et tombe en cuisine juste avant
                    </span>
                  </span>
                </label>
                {info.scheduledMode === 'AUTO' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 0 30px' }}>
                    <span style={{ fontSize: '.85rem', color: 'var(--ink-dim)', fontWeight: 700 }}>Envoyer en cuisine</span>
                    <input
                      className="inp"
                      type="number"
                      min={0}
                      max={180}
                      style={{ width: 80, textAlign: 'center', fontWeight: 800 }}
                      value={info.scheduledLeadMin ?? 20}
                      onChange={(e) => setInfo({ ...info, scheduledLeadMin: Math.max(0, Math.min(180, parseInt(e.target.value, 10) || 0)) })}
                    />
                    <span style={{ fontSize: '.85rem', color: 'var(--ink-dim)', fontWeight: 700 }}>minutes avant l’heure demandée</span>
                  </div>
                )}
              </div>

              <button className="btn-gold" style={{ marginTop: 10, width: 'auto', padding: '10px 22px', fontSize: '1rem' }} disabled={saving} onClick={save}>
                💾 ENREGISTRER
              </button>
            </div>
          )}
        </div>

        {/* ── Autres sections (à venir) ── */}
        <div className="staff-subtitle" style={{ margin: '4px 2px 10px', fontWeight: 900, letterSpacing: '.06em' }}>
          AUTRES MODULES
        </div>
        <div className="role-grid">
          {SECTIONS.map((s) => {
            const target: 'carte' | 'categories' | 'stock' | 'shopping' | 'loyalty' | 'promos' | 'partners' | 'cloture' | 'audit' | 'staff' | 'messaging' | 'printers' | 'readers' | 'delivery' | null =
              s.t === 'Carte' ? 'carte' : s.t === 'Catégories' ? 'categories' : s.t === 'Stock' ? 'stock' : s.t === 'Courses' ? 'shopping' : s.t === 'Fidélité' ? 'loyalty' : s.t === 'Promotions' ? 'promos' : s.t === 'Partenaires' ? 'partners' : s.t === 'Clôture' ? 'cloture' : s.t === 'Audit' ? 'audit' : s.t === 'Comptes & droits' ? 'staff' : s.t === 'Campagne SMS' ? 'messaging' : s.t === 'Imprimantes' ? 'printers' : s.t === 'Terminaux CB' ? 'readers' : s.t === 'Livraison' ? 'delivery' : null;
            return (
              <div
                key={s.t}
                className="role-btn"
                style={{ cursor: (target || s.t === 'Comptabilité') ? 'pointer' : 'default', opacity: (target || s.t === 'Comptabilité') ? 1 : 0.55 }}
                onClick={target ? () => setView(target) : s.t === 'Comptabilité' ? () => { setDashTab('Comptabilité'); setScreen('dash'); } : undefined}
              >
                <span className="role-ic">{s.icon}</span>
                <span className="role-lbl">{s.t}</span>
                <span className="role-desc">{target ? '✏️ Éditer maintenant' : s.t === 'Comptabilité' ? '📊 Ouvrir' : s.d}</span>
              </div>
            );
          })}
        </div>

        <div className="about">
          <div className="kbar-logo" style={{ fontSize: '1.6rem' }}>{info?.name ?? 'TAJ FUSION'}</div>
          <p style={{ color: 'var(--ink-dim)', fontSize: '.8rem', marginTop: 8 }}>
            Système de caisse connectée — conforme NF525 (art. 286 CGI).
          </p>
          <p style={{ color: 'var(--gold)', fontWeight: 800, marginTop: 12 }}>Logiciel édité par ecommx solutions</p>
          <button className="linkbtn" style={{ marginTop: 16 }} onClick={() => setAuth(null)}>Se déconnecter</button>
        </div>
      </div>
      )}
    </div>
  );
}
